import { getBuilderZIndex, isAlwaysOnTop } from './alwaysOnTop';

// A field laid over a text field is easy to lose on the constructor canvas: its label
// reads as one more line of the paragraph under it, and nothing shows where the field
// starts or ends. So the canvas boxes the "Always on Top" fields drawn over a text field
// - the ones pinned there on purpose - and this works out which ones those are. The
// same field anywhere else on the canvas, or a field over the text without the
// setting, is drawn as usual.
//
// It goes by what the canvas actually draws rather than by the stored options: several
// field types have no stored width and size to their content, and a text field's height
// follows its text.

// Two boxes overlap when they share some area; touching along an edge is not enough, so
// a field placed flush under a paragraph is not counted as sitting on it.
export const rectsOverlap = (a, b) => a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

// The canvas stacks fields by their builder z-index, and two fields on the same z-index
// by their order in the form, the later one on top.
const isDrawnAbove = (box, other) => box.zIndex > other.zIndex || (box.zIndex === other.zIndex && box.order > other.order);

// The ids of the "Always on Top" fields drawn over a text field. Each box is `{ id,
// type, alwaysOnTop, zIndex, order, rect }`. A field under the text is covered by the
// text's white writing area, so only the fields on top of it count - which matters when
// the text is pinned too.
export const getFieldIdsOverText = (boxes) => {
  const textBoxes = boxes.filter((box) => box.type === 'text');

  return new Set(
    boxes
      .filter(
        (box) =>
          box.alwaysOnTop && textBoxes.some((text) => text.id !== box.id && isDrawnAbove(box, text) && rectsOverlap(box.rect, text.rect)),
      )
      .map((box) => box.id),
  );
};

// The part of a field that counts as the field: for a text field its writing area, for
// every other field its content. The box around the content carries 20px of padding
// that is empty, so a field placed just under a paragraph does not reach into it.
const getMeasuredElement = (preview, type) =>
  preview.querySelector(type === 'text' ? '.ql-container' : '.form-builder-element-preview-wrapper');

// Reads the boxes of `fields` off the constructor canvas rendered inside `container`.
// A field that is not on the canvas (yet) is skipped.
export const measureCanvasFields = (container, fields) =>
  fields.reduce((boxes, field, order) => {
    const preview = container.querySelector(`.form-builder-element-${field.id}`);
    const measured = preview && getMeasuredElement(preview, field.type);

    if (measured) {
      boxes.push({
        id: field.id,
        type: field.type,
        alwaysOnTop: isAlwaysOnTop(field),
        zIndex: getBuilderZIndex(field),
        order,
        rect: measured.getBoundingClientRect(),
      });
    }

    return boxes;
  }, []);
